import { useCallback, useState } from 'react';
import { batch } from 'react-redux';

import * as St from './StoresList.styles';

import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useStoresContext } from '~/legacy/easy-estimator-page/context/storesContext';
import { useAppDispatch } from '~/redux/hooks';
import { updateIsNewEstimate } from '~/redux/slices/estimate';
import { updateZipcode } from '~/redux/slices/zipcode';
import RetailStores from '~organisms/RetailStores';
import RetailStoresMap from '~organisms/RetailStoresMap';
import StoreChangeModal from '~organisms/StoreChangeModal';
import StoreSearch from '~templates/StoreSearch';

type StoresListProps = {
  showZipCodeInput?: boolean;
  showStoresList?: boolean;
};

const StoresList = ({
  showZipCodeInput = true,
  showStoresList = true,
}: StoresListProps) => {
  const dispatch = useAppDispatch();
  const [isZipModalVisible, setIsZipModalVisible] = useState(false);
  const [newZipCode, setNewZipCode] = useState<string>();

  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();

  const {
    getters: { googleMapsApiKey, googleMapsMapId },
  } = useStoresContext();

  const closeZipModal = () => {
    setIsZipModalVisible(false);
  };

  const updateZipCodeAndReplaceProducts = useCallback(async () => {
    if (newZipCode) {
      await replaceProductsWithEquivalents({ zipCode: newZipCode });

      dispatch(updateZipcode(newZipCode));
    }

    closeZipModal();
  }, [dispatch, newZipCode, replaceProductsWithEquivalents]);

  const showMap = Boolean(googleMapsApiKey) && Boolean(googleMapsMapId);

  return (
    <St.Container>
      <StoreSearch
        showZipCodeInput={showZipCodeInput}
        onSubmitWithNonEmptyDrawingArea={(newZipcode) => {
          setNewZipCode(newZipcode);
          setIsZipModalVisible(true);
        }}
        onSubmitWithEmptyDrawingArea={(newZipcode) => {
          batch(() => {
            dispatch(updateZipcode(newZipcode));
            dispatch(updateIsNewEstimate(true));
          });
        }}
      />
      {showStoresList && <RetailStores />}
      {showStoresList && showMap && (
        <St.MapContainer>
          <RetailStoresMap />
        </St.MapContainer>
      )}
      <StoreChangeModal
        newZipCode={newZipCode}
        isVisible={isZipModalVisible}
        handleClose={closeZipModal}
        handleChange={updateZipCodeAndReplaceProducts}
      />
    </St.Container>
  );
};

export default StoresList;
